import {useEffect, useState} from 'react';
import {Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle} from '@/components/tailwind/dialog';
import {Button} from '@/components/tailwind/button';
import {Field, FieldGroup, Label} from '@/components/tailwind/fieldset';
import {Input} from '@/components/tailwind/input';
import {Text} from '@/components/tailwind/text';
import {Member} from '@/models/member';
import {useAuth} from '@/context/use-auth';
import {useMembersTranslation} from '@/i18n/hooks';
import {useToast} from '@/hooks/use-toast';
import {donatePointsToSocialAccount, VoluntaryPointDonationResult} from '@/services/social-account-services';
import {ProblemDetail} from '@/types/problem-details';

interface SocialAccountDonationDialogProps {
    memberData: Member;
    isOpen: boolean;
    onClose: () => void;
    onDonated: (result: VoluntaryPointDonationResult) => void;
}

const ERROR_PREFIX = 'memberActions.donatePoints.errors';

export default function SocialAccountDonationDialog({
    memberData,
    isOpen,
    onClose,
    onDonated,
}: SocialAccountDonationDialogProps) {
    const {t} = useMembersTranslation();
    const {token} = useAuth();
    const {toast} = useToast();

    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [errorKey, setErrorKey] = useState<string | null>(null);
    const [errorArgs, setErrorArgs] = useState<Record<string, string> | undefined>(undefined);
    const [submitting, setSubmitting] = useState(false);

    const currentBalance = memberData.memberPoints?.points ?? 0;

    useEffect(() => {
        if (isOpen) {
            setAmount('');
            setNote('');
            setErrorKey(null);
            setErrorArgs(undefined);
            setSubmitting(false);
        }
    }, [isOpen]);

    function setError(key: string, args?: Record<string, string>) {
        setErrorKey(key);
        setErrorArgs(args);
    }

    async function handleConfirm() {
        const parsedAmount = Number(amount);
        if (!Number.isInteger(parsedAmount) || parsedAmount < 1) {
            setError(`${ERROR_PREFIX}.amount-min`);
            return;
        }

        setSubmitting(true);
        setErrorKey(null);
        setErrorArgs(undefined);
        try {
            const result = await donatePointsToSocialAccount(
                token,
                memberData.id,
                parsedAmount,
                note.trim() || undefined,
            );
            if (result) {
                toast({
                    title: t('memberActions.donatePoints.toast.success.title'),
                    description: t('memberActions.donatePoints.toast.success.description', {points: parsedAmount}),
                    variant: 'default',
                });
                onDonated(result);
                onClose();
            }
        } catch (reason) {
            const problem = reason as ProblemDetail;
            const finding = problem?.validationIssues?.[0];
            if (finding) {
                setError(finding.message, finding.args);
            } else {
                setError(`${ERROR_PREFIX}.generic`);
            }
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Dialog open={isOpen} onClose={onClose}>
            <DialogTitle data-testid="donation-dialog-title">
                {t('memberActions.donatePoints.dialog.title')}
            </DialogTitle>
            <DialogDescription>
                {t('memberActions.donatePoints.dialog.description')}
            </DialogDescription>
            <DialogBody>
                <FieldGroup>
                    <Text className="text-gray-600 dark:text-gray-400 text-sm" data-testid="donation-current-balance">
                        {t('memberActions.donatePoints.dialog.currentBalance', {points: currentBalance})}
                    </Text>
                    <Field>
                        <Label htmlFor="donation-amount">
                            {t('memberActions.donatePoints.dialog.amountLabel')}
                        </Label>
                        <Input
                            id="donation-amount"
                            type="number"
                            min={1}
                            value={amount}
                            onChange={(e) => {
                                setAmount(e.target.value);
                                setErrorKey(null);
                            }}
                            placeholder={t('memberActions.donatePoints.dialog.amountPlaceholder')}
                            invalid={!!errorKey}
                            data-testid="donation-amount-input"
                        />
                    </Field>
                    <Field>
                        <Label htmlFor="donation-note">
                            {t('memberActions.donatePoints.dialog.noteLabel')}
                        </Label>
                        <Input
                            id="donation-note"
                            type="text"
                            maxLength={200}
                            value={note}
                            onChange={(e) => {
                                setNote(e.target.value);
                                setErrorKey(null);
                            }}
                            placeholder={t('memberActions.donatePoints.dialog.notePlaceholder')}
                            data-testid="donation-note-input"
                        />
                    </Field>
                    {errorKey && (
                        <p className="text-red-600 dark:text-red-400 text-sm" data-testid="donation-error">
                            {t(errorKey, errorArgs)}
                        </p>
                    )}
                </FieldGroup>
            </DialogBody>
            <DialogActions>
                <Button
                    plain
                    onClick={onClose}
                    aria-label={t('memberActions.donatePoints.dialog.cancel.ariaLabel')}
                    data-testid="donation-dialog-cancel"
                >
                    {t('memberActions.donatePoints.dialog.cancel.text')}
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={submitting}
                    aria-label={t('memberActions.donatePoints.dialog.submit.ariaLabel')}
                    data-testid="donation-dialog-confirm"
                >
                    {t('memberActions.donatePoints.dialog.submit.text')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
